import React from 'react';
import { Link } from 'react-router-dom';
import { Separator } from './ui/separator';
import { MapPin, Mail, Globe, Linkedin, Twitter } from 'lucide-react';
import logo from '../assets/images/Logo_Probuild.png';
import { useTranslation } from '../hooks/useTranslation';

const Footer = () => {
  const { t } = useTranslation();
  
  const footerSections = [
    {
      title: t('footer.solutions.title'),
      links: [
        { label: t('footer.solutions.importExport'), href: '#solutions' },
        { label: t('footer.solutions.distribution'), href: '#solutions' },
        { label: t('footer.solutions.b2bPlatform'), href: '#solutions' },
        { label: t('footer.solutions.logisticsManagement'), href: '#solutions' },
        { label: t('footer.solutions.smartBuilding'), href: '#solutions' }
      ]
    },
    {
      title: t('footer.materials.title'),
      links: [
        { label: t('footer.materials.steelMetal'), href: '#materials' },
        { label: t('footer.materials.concreteBlocks'), href: '#materials' },
        { label: t('footer.materials.insulation'), href: '#materials' },
        { label: t('footer.materials.glassFacades'), href: '#materials' },
        { label: t('footer.materials.electricalSystems'), href: '#materials' }
      ]
    },
    {
      title: t('footer.company.title'),
      links: [
        { label: t('footer.company.aboutUs'), href: '#about' },
        { label: t('footer.company.ourPartners'), href: '#partners' },
        { label: t('footer.company.qualityStandards'), href: '#' },
        { label: t('footer.company.sustainability'), href: '#' },
        { label: t('footer.company.careers'), href: '#' }
      ]
    },
    {
      title: t('footer.support.title'),
      links: [
        { label: t('footer.support.contactUs'), href: '#contact' },
        { label: t('footer.support.technicalSupport'), href: '#' },
        { label: t('footer.support.documentation'), href: '#' },
        { label: t('footer.support.faq'), href: '#' },
        { label: t('footer.support.termsConditions'), href: '#' }
      ]
    }
  ];

  const contactInfo = [
    {
      icon: MapPin,
      text: 'THE BLACK CHURCH, ST. MARY\'S PLACE, DUBLIN 7, D07 P4AX, IRELAND'
    },
    {
      icon: Mail,
      text: 'contact@probuildmarket.net',
      href: 'mailto:contact@probuildmarket.net'
    }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        {/* Main Footer Content */}
        <div className="grid lg:grid-cols-6 gap-12 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src={logo}
                alt="PROBUILD MARKET" 
                className="h-20 w-auto"
              />
            </div>
            
            <p className="text-gray-300 leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            
            <div className="text-sm text-gray-400 mb-6 italic">
              PROBUILD MARKET LIMITED
            </div>

            {/* Contact Info */}
            <div className="space-y-3">
              {contactInfo.map((info, index) => {
                const IconComponent = info.icon;
                return (
                  <div key={index} className="flex items-start space-x-3 text-sm text-gray-300">
                    <IconComponent size={16} className="mt-0.5 text-gray-400 flex-shrink-0" />
                    <span>{info.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer Links */}
          {footerSections.map((section, index) => (
            <div key={index}>
              <h3 className="font-medium text-white mb-4">
                {section.title}
              </h3>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-white text-sm transition-colors duration-200"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <Separator className="bg-gray-700 mb-8" />

        {/* Bottom Footer */}
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-8 text-sm text-gray-400">
            <span>{t('footer.copyright')}</span>
            <div className="flex space-x-6">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">
                {t('footer.privacyPolicy')}
              </Link>
              <Link to="/cookie-policy" className="hover:text-white transition-colors">
                {t('footer.cookiePolicy')}
              </Link>
              <Link to="/legal-notice" className="hover:text-white transition-colors">
                {t('footer.legalNotice')}
              </Link>
            </div>
          </div>

          {/* Social Links */}
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-400 mr-2">{t('footer.followUs')}</span>
            <a
             href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin size={16} className="text-gray-300" />
            </a>
            <a
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors"
              aria-label="Twitter"
            >
              <Twitter size={16} className="text-gray-300" />
            </a>
            <a
              href="https://probuildmarket.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors"
              aria-label="Website"
            >
              <Globe size={16} className="text-gray-300" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;